import { render, scene } from '@/threejs/core';
import * as THREE from 'three';

/**
 * BackgroundManager - Centralized management for scene background settings
 * Handles background color, gradient backgrounds, and background textures
 */
class BackgroundManager {
  constructor() {
    this.backgroundColor = '#000000';
    this.backgroundColor2 = '#ffffff';
    this.isGradient = false;
    this.currentTexture = null;
    this.isInitialized = false;
    this.gradientMesh = null;
    this.gradientMaterial = null;
  }

  /**
   * Initialize the background manager
   */
  initialize() {
    this.isInitialized = true;
    this.createGradientMesh();
    return this;
  }

  /**
   * Check if manager is initialized
   * @returns {boolean}
   */
  isManagerInitialized() {
    return this.isInitialized;
  }

  /**
   * Create gradient mesh with shader material
   */
  createGradientMesh() {
    // Dispose existing mesh if it exists
    if (this.gradientMesh) {
      scene.remove(this.gradientMesh);
      if (this.gradientMaterial) {
        this.gradientMaterial.dispose();
      }
      if (this.gradientMesh.geometry) {
        this.gradientMesh.geometry.dispose();
      }
    }

    // Create gradient shader
    const gradientShader = {
      uniforms: {
        centerColor: { value: new THREE.Color(this.centerColor) },
        edgeColor: { value: new THREE.Color(this.edgeColor) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.999999, 1.0); // Render at far plane
        }
      `,
      fragmentShader: `
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        varying vec2 vUv;

        void main() {
          vec2 center = vec2(0.5, 0.5);
          float dist = distance(vUv, center) * 1.4142; // sqrt(2) for corner distance
          vec3 color = mix(centerColor, edgeColor, smoothstep(0.0, 1.0, dist));
          gl_FragColor = vec4(color, 1.0);
        }
      `,
    };

    this.gradientMaterial = new THREE.ShaderMaterial(gradientShader);
    this.gradientMaterial.depthTest = false;
    this.gradientMaterial.depthWrite = false;

    const gradientGeometry = new THREE.PlaneGeometry(2, 2);
    this.gradientMesh = new THREE.Mesh(gradientGeometry, this.gradientMaterial);
    this.gradientMesh.renderOrder = -999999; // Render first
    this.gradientMesh.frustumCulled = false;
    this.gradientMesh.visible = false; // Hidden by default

    // Add to scene
    scene.add(this.gradientMesh);
  }

  /**
   * Set gradient center color
   * @param {string|number} color - Center color
   */
  setGradientCenterColor(color) {
    this.centerColor = color;
    if (this.gradientMaterial) {
      this.gradientMaterial.uniforms.centerColor.value.set(color);
      render();
    }
  }

  /**
   * Set gradient edge color
   * @param {string|number} color - Edge color
   */
  setGradientEdgeColor(color) {
    this.edgeColor = color;
    if (this.gradientMaterial) {
      this.gradientMaterial.uniforms.edgeColor.value.set(color);
      render();
    }
  }

  /**
   * Toggle gradient visibility
   * @param {boolean} visible - Whether gradient should be visible
   */
  setGradientVisible(visible) {
    if (this.gradientMesh) {
      this.gradientMesh.visible = visible;
      render();
    }
  }

  /**
   * Set background color
   * @param {string|number} color - Background color
   */
  setBackgroundColor(color) {
    this.backgroundColor = color;
    this.updateBackground();
  }

  /**
   * Set second background color (for gradient)
   * @param {string|number} color - Second background color
   */
  setBackgroundColor2(color) {
    this.backgroundColor2 = color;
    if (this.isGradient) {
      this.updateBackground();
    }
  }

  /**
   * Set gradient mode
   * @param {boolean} enabled - Whether gradient is enabled
   */
  setGradientEnabled(enabled) {
    this.isGradient = enabled;
    this.updateBackground();
  }

  /**
   * Create gradient texture for background
   * @param {string|number} color1 - First color
   * @param {string|number} color2 - Second color
   * @returns {THREE.CanvasTexture} The gradient texture
   */
  createGradientTexture(color1, color2) {
    color1 = color1.startsWith('#') ? color1 : `#${color1}`;
    color2 = color2.startsWith('#') ? color2 : `#${color2}`;
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;

    const context = canvas.getContext('2d');

    // Create vertical gradient from top to bottom
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, color1);
    gradient.addColorStop(1, color2);

    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);

    const texture = new THREE.CanvasTexture(canvas);
    texture.mapping = THREE.EquirectangularReflectionMapping;
    return texture;
  }

  /**
   * Update scene background based on current settings
   */
  updateBackground() {
    // Dispose of previous texture if it exists
    if (this.currentTexture) {
      this.currentTexture.dispose();
      this.currentTexture = null;
    }

    if (this.isGradient) {
      // Create and apply gradient background
      this.currentTexture = this.createGradientTexture(this.backgroundColor, this.backgroundColor2);
      scene.background = this.currentTexture;
    } else {
      // Apply solid color background
      const color = this.backgroundColor.startsWith('#') ? this.backgroundColor : `#${this.backgroundColor}`;

      scene.background = new THREE.Color(color);
    }

    render();
  }

  /**
   * Set background from texture
   * @param {THREE.Texture} texture - Background texture
   */
  setBackgroundTexture(texture) {
    if (this.currentTexture) {
      this.currentTexture.dispose();
    }
    this.currentTexture = texture;
    scene.background = texture;
    render();
  }

  /**
   * Remove background (set to null)
   */
  clearBackground() {
    if (this.currentTexture) {
      this.currentTexture.dispose();
      this.currentTexture = null;
    }
    scene.background = null;
    render();
  }

  /**
   * Handle background setting changes
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   */
  handleBackgroundSetting(key, value) {
    switch (key) {
      case 'render.background':
        this.setBackgroundColor(value);
        this.setGradientCenterColor(value);
        break;
      case 'render.background2':
        this.setGradientEdgeColor(value);
        // this.setBackgroundColor2(value)
        break;
      case 'render.gradient':
        this.setGradientVisible(value);
        // this.setGradientEnabled(value)
        break;
      default:
        console.warn(`Unknown background setting: ${key}`);
    }
  }

  /**
   * Get current background configuration
   * @returns {object} Current background settings
   */
  getBackgroundConfig() {
    return {
      backgroundColor: this.backgroundColor,
      backgroundColor2: this.backgroundColor2,
      isGradient: this.isGradient,
      hasTexture: this.currentTexture !== null,
      gradientVisible: this.gradientMesh ? this.gradientMesh.visible : false,
    };
  }

  /**
   * Dispose of background resources
   */
  dispose() {
    if (this.currentTexture) {
      this.currentTexture.dispose();
      this.currentTexture = null;
    }
    if (this.gradientMesh) {
      scene.remove(this.gradientMesh);
      if (this.gradientMaterial) {
        this.gradientMaterial.dispose();
        this.gradientMaterial = null;
      }
      if (this.gradientMesh.geometry) {
        this.gradientMesh.geometry.dispose();
      }
      this.gradientMesh = null;
    }
    this.isInitialized = false;
  }
}

// Create and export singleton instance
export const backgroundManager = new BackgroundManager();

// Export the class for testing or multiple instances if needed
export { BackgroundManager };
